import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { formatMoney } from '../ui/format';
import { Badge, EmptyState, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import {
  getForecast,
  listDealsNeedingAttention,
  type AttentionReason,
  type DealAttention,
  type ForecastMonth,
} from './api';

/**
 * What the month is going to be, and what somebody has let slip (Phase 120).
 *
 * The two halves are on one screen because they are the same conversation: a forecast is only worth
 * reading next to the deals that are quietly not going to happen. A number on its own invites
 * somebody to plan around it; the list underneath is the part that changes the number.
 */

const REASON_TONE: Record<AttentionReason, BadgeTone> = {
  OVERDUE: 'danger',
  CLOSING_BLIND: 'danger',
  STUCK: 'warning',
  SILENT: 'warning',
  NO_NEXT_STEP: 'info',
  NO_CONTACT: 'neutral',
  UNOWNED: 'neutral',
};

const MONTHS = [3, 6, 12];

export function ForecastPage(): React.JSX.Element {
  const [months, setMonths] = useState(3);

  const forecast = useQuery({
    queryKey: ['forecast', months],
    queryFn: () => getForecast(months),
  });

  const attention = useQuery({
    queryKey: ['deals-attention'],
    queryFn: () => listDealsNeedingAttention(20),
  });

  const totals = forecast.data?.totals;

  return (
    <div className="view">
      <PageHeader
        title={t('forecast.title')}
        description={t('forecast.description')}
        actions={
          <select
            className="select select--compact"
            value={months}
            onChange={(event) => setMonths(Number(event.target.value))}
            aria-label={t('forecast.window')}
          >
            {MONTHS.map((value) => (
              <option key={value} value={value}>
                {t('forecast.months').replace('{n}', String(value))}
              </option>
            ))}
          </select>
        }
      />

      <Panel title={t('forecast.summary')}>
        <div className="stat-row">
          <Stat label={t('forecast.won')} value={formatMoney(totals?.wonCents ?? 0)} />
          <Stat
            label={t('forecast.weighted')}
            value={formatMoney(totals?.weightedCents ?? 0)}
            hint={t('forecast.weightedHint')}
          />
          <Stat
            label={t('forecast.bestCase')}
            value={formatMoney(totals?.bestCaseCents ?? 0)}
            hint={t('forecast.bestCaseHint')}
          />

          {/* Only when somebody set a target: against zero it would read as "covered nothing". */}
          {forecast.data?.targetCents != null && (
            <Stat
              label={t('forecast.coverage')}
              value={`${Math.round((totals?.coverage ?? 0) * 100)}%`}
              hint={t('forecast.coverageHint').replace(
                '{target}',
                formatMoney(forecast.data.targetCents),
              )}
            />
          )}
        </div>

        {forecast.data?.targetCents == null && (
          <p className="muted small">
            {t('forecast.noTarget')}{' '}
            <Link to="/configuracoes/empresa">{t('forecast.noTargetLink')}</Link>
          </p>
        )}
      </Panel>

      <Panel title={t('forecast.byMonth')}>
        {(forecast.data?.months?.length ?? 0) === 0 ? (
          <EmptyState title={t('forecast.emptyTitle')} />
        ) : (
          <MonthBars months={forecast.data!.months!} />
        )}
      </Panel>

      <Panel
        title={t('forecast.attention')}
        description={
          attention.data
            ? t('forecast.attentionScanned').replace('{n}', String(attention.data.scanned))
            : undefined
        }
      >
        {attention.isLoading ? (
          <p className="muted">{t('state.loading')}</p>
        ) : (attention.data?.deals?.length ?? 0) === 0 ? (
          <EmptyState
            title={t('forecast.attentionEmptyTitle')}
            description={t('forecast.attentionEmptyBody')}
          />
        ) : (
          <ul className="attention">
            {attention.data!.deals!.map((deal) => (
              <AttentionRow key={deal.id} deal={deal} />
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): React.JSX.Element {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      {hint && <span className="muted small">{hint}</span>}
    </div>
  );
}

/**
 * The months, as bars.
 *
 * Drawn from the largest best case rather than from the largest weighted figure, so the three
 * numbers in a month stay comparable with each other and with the months beside them — a chart
 * that rescales per month is a chart that makes a bad month look like a good one.
 */
function MonthBars({ months }: { months: ForecastMonth[] }): React.JSX.Element {
  const ceiling = Math.max(1, ...months.map((month) => month.bestCaseCents + month.wonCents));

  return (
    <ul className="forecast-months">
      {months.map((month) => (
        <li key={month.month}>
          <span className="forecast-months__label">{monthLabel(month.month)}</span>

          <span className="forecast-months__track" aria-hidden="true">
            <span
              className="forecast-months__bar forecast-months__bar--won"
              style={{ width: `${(month.wonCents / ceiling) * 100}%` }}
            />
            <span
              className="forecast-months__bar forecast-months__bar--weighted"
              style={{ width: `${(month.weightedCents / ceiling) * 100}%` }}
            />
            <span
              className="forecast-months__bar forecast-months__bar--best"
              style={{ width: `${(month.bestCaseCents / ceiling) * 100}%` }}
            />
          </span>

          <span className="forecast-months__numbers muted small">
            {t('forecast.won')}: {formatMoney(month.wonCents)} · {t('forecast.weighted')}:{' '}
            {formatMoney(month.weightedCents)} · {t('forecast.bestCase')}:{' '}
            {formatMoney(month.bestCaseCents)}
          </span>
        </li>
      ))}
    </ul>
  );
}

function monthLabel(month: string): string {
  const [year, index] = month.split('-');
  const date = new Date(Date.UTC(Number(year), Number(index) - 1, 1));

  return date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit', timeZone: 'UTC' });
}

function AttentionRow({ deal }: { deal: DealAttention }): React.JSX.Element {
  return (
    <li className="attention__row">
      <span className="attention__main">
        <Link className="strong" to={`/crm/oportunidades/${deal.id}`}>
          {deal.title}
        </Link>
        <span className="muted small">
          {deal.stageName} · {t('forecast.idle').replace('{n}', String(deal.idleDays))}
        </span>
      </span>

      <span className="attention__reasons">
        {deal.reasons.map((reason) => (
          <Badge key={reason} tone={REASON_TONE[reason]}>
            {t(`forecast.reason.${reason}`)}
          </Badge>
        ))}
      </span>

      <span className="attention__value strong">{formatMoney(deal.amountCents)}</span>
    </li>
  );
}
